import React, { useState, createContext, useContext, useEffect } from "react";
import { useApiService } from "../shared/apiservice";
import { StockExpiryWarning } from "../shared/types";

export type SortOrder = "asc" | "desc";

const stockWarningsContext = createContext<{
    stockExpiryWarnings: StockExpiryWarning[];
    errors: string[];
    loading: boolean;
    page: number;
    perPage: number;
    total: number;
    searchTerm: string;
    sortField: string;
    sortOrder: SortOrder;
    fetchStockWarnings: () => void;
    setPage: (page: number) => void;
    setPerPage: (perPage: number) => void;
    setSearchTerm: (term: string) => void;
    setSort: (field: string) => void;
}>({
    stockExpiryWarnings: [],
    errors: [],
    loading: false,
    page: 1,
    perPage: 15,
    total: 0,
    searchTerm: "",
    sortField: "warning_level",
    sortOrder: "desc",
    fetchStockWarnings: () => { },
    setPage: () => { },
    setPerPage: () => { },
    setSearchTerm: () => { },
    setSort: () => { },
});

function parseStockWarning(d: any): StockExpiryWarning {
    return {
        ...d,
        product: d.product,
        warning_level: d.warning_level,
        expires_at: new Date(d.expires_at),
    };
}

function useProviderStockWarnings() {
    const [stockExpiryWarnings, setStockExpiryWarnings] = useState<StockExpiryWarning[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPageInternal] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [total, setTotal] = useState(0);
    const [searchTerm, setSearchTermInternal] = useState("");
    const [sortField, setSortField] = useState("warning_level");
    const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
    const apiService = useApiService();

    function setSearchTerm(term: string) {
        setSearchTermInternal(term);
        setPageInternal(1);
    }

    function setPage(p: number) {
        setPageInternal(p);
    }

    function setSort(field: string) {
        if (field === sortField) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortOrder("asc");
        }
        setPageInternal(1);
    }

    useEffect(() => {
        fetchStockWarnings();
    }, [page, perPage, searchTerm, sortField, sortOrder]);

    async function fetchStockWarnings() {
        setLoading(true);
        setErrors([]);

        try {
            const params = new URLSearchParams({
                page: String(page),
                per_page: String(perPage),
                sort: sortField,
                order: sortOrder,
            });
            if (searchTerm.trim()) {
                params.set("q", searchTerm.trim());
            }
            const response = await apiService.get(
                `/stock/warnings/expiry?${params.toString()}`
            );
            if (!response.ok) {
                setErrors([
                    'Loading stock expiry warnings failed. Please try again.',
                ]);
                return;
            }
            const json = await response.json();
            const data: StockExpiryWarning[] = (json.data ?? json).map(parseStockWarning);
            setTotal(json.meta.total ?? data.length);
            setStockExpiryWarnings(data);
        } catch (error) {
            console.error(error);
            setErrors([
                'Loading stock expiry warnings failed. Please try again.',
            ]);
        } finally {
            setLoading(false);
        }
    }

    return {
        stockExpiryWarnings,
        errors,
        loading,
        page,
        perPage,
        total,
        searchTerm,
        sortField,
        sortOrder,
        fetchStockWarnings,
        setPage,
        setPerPage,
        setSearchTerm,
        setSort,
    }
}

export function ProviderStockWarnings({ children }: { children: React.ReactNode }) {
    const stockWarnings = useProviderStockWarnings();
    return <stockWarningsContext.Provider value={stockWarnings}> {children}</stockWarningsContext.Provider>;
}

export const useStockWarnings = () => {
    return useContext(stockWarningsContext);
}
